// What search engines and link previews read from the public site: schema.org JSON-LD
// and sitemap.xml. Pure builders; apps/web passes its public address (VITE_SITE_URL).

import { bodyLabels, fuelLabels, priceAmount } from "./catalog-config";
import type { VehicleDto } from "./routers/content";

const SITE_NAME = "تابان خودرو";

/** A site path (or an already absolute URL, like a bucket image) as an absolute URL */
export const absoluteUrl = (siteUrl: string, path: string) => new URL(path, siteUrl).href;

export type Crumb = { name: string; path: string };

export const breadcrumbJsonLd = (siteUrl: string, crumbs: Crumb[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((crumb, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: crumb.name,
    item: absoluteUrl(siteUrl, crumb.path),
  })),
});

export type JsonLdVehicle = Pick<
  VehicleDto,
  | "id"
  | "name"
  | "nameLatin"
  | "brand"
  | "trim"
  | "year"
  | "price"
  | "fuel"
  | "body"
  | "available"
  | "image"
  | "gallery"
>;

/**
 * A model as a Product + Car with its Offer (Google's vehicle listing shape). The site
 * sells new cars only. Prices are stored in toman, which is not an ISO 4217 currency,
 * so the offer is in rials: IRR = toman × 10. A model priced on request has no offer.
 */
export function vehicleJsonLd(siteUrl: string, vehicle: JsonLdVehicle, description: string) {
  const url = absoluteUrl(siteUrl, `/vehicles/${vehicle.id}`);
  const amount = priceAmount(vehicle.price);
  return {
    "@context": "https://schema.org",
    "@type": ["Product", "Car"],
    name: `${vehicle.name} ${vehicle.trim}`.trim(),
    // the Latin spelling of the same model: what a search engine needs to tell «تویوتا کمری»
    // and Toyota Camry are one car
    ...(vehicle.nameLatin && { alternateName: vehicle.nameLatin }),
    description,
    url,
    image: [vehicle.image, ...vehicle.gallery]
      .filter(Boolean)
      .map((image) => absoluteUrl(siteUrl, image)),
    brand: { "@type": "Brand", name: vehicle.brand },
    model: vehicle.name,
    vehicleModelDate: String(vehicle.year),
    fuelType: fuelLabels[vehicle.fuel],
    bodyType: bodyLabels[vehicle.body],
    itemCondition: "https://schema.org/NewCondition",
    ...(amount !== null && {
      offers: {
        "@type": "Offer",
        url,
        price: amount * 10,
        priceCurrency: "IRR",
        availability: vehicle.available
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        itemCondition: "https://schema.org/NewCondition",
        seller: { "@type": "AutoDealer", name: SITE_NAME, url: absoluteUrl(siteUrl, "/") },
      },
    }),
  };
}

export type DealerBranch = { name: string; address: string; phone: string };
export type Dealer = { description: string; telephone: string; branches: DealerBranch[] };

/** The stable name search engines give the business, referenced from the other entities */
export const dealerId = (siteUrl: string) => `${absoluteUrl(siteUrl, "/")}#dealer`;

/**
 * The dealership on the homepage: the sales line and every active branch as a department.
 * The first branch is the head office, so its address is also the business's own: a
 * LocalBusiness without a top-level postal address is much weaker in local results.
 */
export function dealerJsonLd(siteUrl: string, dealer: Dealer) {
  const head = dealer.branches[0];
  const postal = (streetAddress: string) => ({
    "@type": "PostalAddress",
    streetAddress,
    addressCountry: "IR",
  });
  return {
    "@context": "https://schema.org",
    "@type": "AutoDealer",
    "@id": dealerId(siteUrl),
    name: SITE_NAME,
    description: dealer.description,
    url: absoluteUrl(siteUrl, "/"),
    logo: absoluteUrl(siteUrl, "/logo.png"),
    image: absoluteUrl(siteUrl, "/logo.png"),
    areaServed: { "@type": "Country", name: "IR" },
    ...(dealer.telephone && { telephone: dealer.telephone }),
    ...(head && { address: postal(head.address) }),
    department: dealer.branches.map((branch) => ({
      "@type": "AutoDealer",
      name: branch.name,
      ...(branch.phone && { telephone: branch.phone }),
      address: postal(branch.address),
    })),
  };
}

/**
 * The site itself. Google reads WebSite.name for the site name shown above a result, and
 * without it falls back to guessing from the title and the domain. No SearchAction: the
 * site's search runs in the browser and has no address of its own to hand a query to.
 */
export const websiteJsonLd = (siteUrl: string) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${absoluteUrl(siteUrl, "/")}#website`,
  name: SITE_NAME,
  url: absoluteUrl(siteUrl, "/"),
  inLanguage: "fa-IR",
  publisher: { "@id": dealerId(siteUrl) },
});

/**
 * The public pages every visitor can open, each with how much of the site it carries
 * (priority) and how often it is worth coming back to. /vehicles/<id> pages come from
 * the catalog. /login, /panel, /pay and /d/<token> are private and stay out.
 */
export const sitemapStaticPaths: { path: string; priority: number; changefreq: string }[] = [
  { path: "/", priority: 1, changefreq: "daily" },
  { path: "/vehicles", priority: 0.9, changefreq: "daily" },
  { path: "/guide", priority: 0.7, changefreq: "monthly" },
  { path: "/compare", priority: 0.6, changefreq: "weekly" },
  { path: "/contact", priority: 0.6, changefreq: "monthly" },
  { path: "/about", priority: 0.5, changefreq: "monthly" },
  { path: "/terms", priority: 0.2, changefreq: "yearly" },
  { path: "/privacy", priority: 0.2, changefreq: "yearly" },
  { path: "/accessibility", priority: 0.2, changefreq: "yearly" },
];

const xmlEscape = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");

export type SitemapVehicle = { id: string; name: string; image: string };

/**
 * sitemap.xml of the static pages plus one page per listed model. Each model page carries
 * its photo as an <image:image>, which is how the catalog's photos reach Google Images —
 * a real share of the traffic a dealership gets, and nothing on the page itself announces them.
 */
export function sitemapXml(siteUrl: string, vehicles: SitemapVehicle[]) {
  const tag = (name: string, value: string) => `<${name}>${xmlEscape(value)}</${name}>`;
  const entry = (path: string, priority: number, changefreq: string, extra = "") =>
    `  <url>${tag("loc", absoluteUrl(siteUrl, path))}${tag("changefreq", changefreq)}` +
    `${tag("priority", priority.toFixed(1))}${extra}</url>`;
  const urls = [
    ...sitemapStaticPaths.map((page) => entry(page.path, page.priority, page.changefreq)),
    ...vehicles.map((vehicle) =>
      entry(
        `/vehicles/${vehicle.id}`,
        0.8,
        "weekly",
        vehicle.image
          ? `<image:image>${tag("image:loc", absoluteUrl(siteUrl, vehicle.image))}` +
              `${tag("image:title", vehicle.name)}</image:image>`
          : "",
      ),
    ),
  ].join("\n");
  return (
    `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"` +
    ` xmlns:image="http://www.google.com/schemas/sitemap-image/1.1">\n${urls}\n</urlset>\n`
  );
}

/** robots.txt: private pages closed to crawlers, the sitemap announced */
export const robotsTxt = (siteUrl: string) =>
  [
    "User-agent: *",
    "Disallow: /panel",
    "Disallow: /login",
    "Disallow: /pay",
    // a payment link sent to one buyer by SMS; the page is noindex too
    "Disallow: /d/",
    "",
    `Sitemap: ${absoluteUrl(siteUrl, "/sitemap.xml")}`,
    "",
  ].join("\n");
